import { ref, onMounted, onBeforeUnmount } from 'vue'

declare const __BUILD_ID__: string

const POLL_INTERVAL_MS = 60_000 // every 60s
// Base-path aware: the apply build ships with VITE_BASE=/apply/, and its
// version.txt lives under that base. Polling bare /version.txt on
// portal.claimshieldpro.com hits the PORTAL app's SPA fallback (HTML), which
// never equals our build ID — every driver saw a permanent "update available"
// banner. BASE_URL always ends with '/'.
const VERSION_URL = `${import.meta.env.BASE_URL}version.txt`

/**
 * Proactively detects when a new build has been deployed by polling /version.txt.
 *
 * The build ID is stamped into the JS bundle at build time (__BUILD_ID__) and also
 * served as a static file at /version.txt. When they diverge, a newer build is live
 * and the user should refresh between steps.
 *
 * Polling stops once an update is detected; the banner stays visible until the user
 * acts on it.
 */
export function useVersionCheck() {
  const updateAvailable = ref(false)
  const currentBuildId = __BUILD_ID__
  const remoteBuildId = ref<string | null>(null)
  let intervalId: ReturnType<typeof setInterval> | null = null
  let stopped = false

  async function checkVersion() {
    if (stopped || updateAvailable.value) return
    try {
      // Bust caches — we always want the freshest version.txt
      const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' })
      if (!res.ok) return
      const next = (await res.text()).trim()
      if (next && next !== currentBuildId) {
        remoteBuildId.value = next
        updateAvailable.value = true
        // Stop polling once we know an update is available
        if (intervalId) {
          clearInterval(intervalId)
          intervalId = null
        }
      }
    } catch {
      // Network error — quietly ignore, try again next tick
    }
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') checkVersion()
  }

  onMounted(() => {
    // Run one check immediately, then on interval
    checkVersion()
    intervalId = setInterval(checkVersion, POLL_INTERVAL_MS)
    // Also re-check when the tab regains focus (laptop reopened, etc.)
    document.addEventListener('visibilitychange', onVisibilityChange)
  })

  onBeforeUnmount(() => {
    stopped = true
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    document.removeEventListener('visibilitychange', onVisibilityChange)
  })

  function refreshNow() {
    window.location.reload()
  }

  return { updateAvailable, refreshNow, currentBuildId, remoteBuildId }
}
